import { FC, FormEventHandler, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { shouldOfferReeferDetails } from 'utils';

import { Damages, useDamage } from './damages';
import { EquipmentInfo } from './equipment';
import {
  ChassisDetails,
  ContainerDetails,
  GeneralInfo,
  InspectionSummary,
  ReeferInfo,
  TruckingInfo
} from './form-components';
import { StackInfo } from './stacks';
import { TireInfo, useTireInspections } from './tires';

import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useSizeTypes } from 'contexts';
import { useResponse } from 'contexts/response';
import { useNewTransaction } from '.';
import { CancelConfirmation } from './dialogs';
import TrailerDetails from './form-components/trailer';
import './new-transaction.scss';
import { UploadImageDialog } from './transaction-images';

const NewTransactionForm: FC = () => {
  const navigate = useNavigate();
  const sizeTypeCatalog = useSizeTypes();
  const { handleError } = useResponse();
  const { bulk, chassis, container, formRef, genset, stack, transaction, saveAsDraft, isEmpty, isTrailer } =
    useNewTransaction();
  const { damages } = useDamage();
  const { tires } = useTireInspections();

  const [dialog, setDialog] = useState(false);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    e => {
      e.preventDefault();
      if (!e.currentTarget.reportValidity()) return;
      [chassis, container, genset, ...stack].some(unit => !unit?.flatbed && !unit.ownerChassis && !!unit?.customer?.id)
        ? navigate(`/gate/review?inbound=${transaction.inbound}&bulk=${bulk}`)
        : handleError('At least one unit account is required');
    },
    [bulk, chassis, container, genset, stack, transaction.inbound, handleError, navigate]
  );

  return (
    <form id="new-transaction" className="d-flex flex-column w-100" onSubmit={handleSubmit} ref={formRef}>
      <div className="d-flex justify-content-between p-3 transaction-header">
        <div className="d-flex g-3 flex-shrink-1">
          <h1 className="text-primary-dark font-weight-bold flex-shrink-1">NEW TRANSACTION</h1>
          <div className="d-flex g-2">
            {!transaction.inbound ? (
              <label className="status gate-out">GATE OUT</label>
            ) : (
              <label className="status gate-in">GATE IN</label>
            )}
            {bulk && <label className="status bulk">BULK</label>}
          </div>
        </div>
        <div className="d-flex g-3">
          <button
            type="button"
            className="action btn-outline d-flex align-items-center g-3 h-100"
            onClick={() => setDialog(true)}
          >
            <span>IMAGE</span>
            <span className="material-icons">upload</span>
          </button>
          <UploadImageDialog open={dialog} onClose={() => setDialog(false)} />
        </div>
      </div>
      <div className="d-flex flex-column flex-fill form-content p-3">
        <div className="card-primary">
          <div className="card-header px-3">
            <h4>EQUIPMENT DETAILS</h4>
          </div>
          <div className="card-body">{!bulk ? <EquipmentInfo /> : <StackInfo />}</div>
        </div>
        {!shouldOfferReeferDetails(
          {
            containerSizeType: container.sizeType,
            gensetSizeType: genset.sizeType,
            gensetUnitNumber: genset.unitNumber,
            bookingRequiresGenset: container.booking?.requiresGenset,
            bookingVentilation: container.booking?.ventilation,
            recordedTemperature: container.temperature,
            recordedVentilation: container.ventilation
          },
          sizeTypeCatalog
        ) ? null : (
          <ReeferInfo />
        )}
        {!container.loaded ? null : <ContainerDetails />}
        <ChassisDetails />
        <TrailerDetails />
        <TruckingInfo />
        <GeneralInfo />
        {!!stack.length ? null : (
          <div className="card-primary">
            <div className="card-header px-3">
              <h4>TIRE DETAILS</h4>
            </div>
            <div className="card-body">
              <TireInfo />
            </div>
          </div>
        )}
        {!transaction.inbound || !!stack.length ? null : (
          <div className="card-primary">
            <div className="card-header px-3">
              <h4>INSPECTION</h4>
            </div>
            <div className="card-body">
              <Damages
                CONTAINER={!!container.unitNumber}
                CHASSIS={!!chassis.unitNumber || isTrailer}
                GENSET={!!genset.unitNumber}
              />
            </div>
          </div>
        )}
        {!damages.length &&
        !Object.values(tires).some(tire => tire && 'damageType' in tire && !!tire.damageType) ? null : (
          <div className="card-primary">
            <div className="card-header px-3">
              <h4>INSPECTION SUMMARY</h4>
            </div>
            <div className="card-body">
              <InspectionSummary />
            </div>
          </div>
        )}
      </div>
      <div className="p-3 transaction-footer">
        <div className="form-actions grid-3">
          <CancelConfirmation />
          <button
            type="button"
            className="action btn-outline"
            onClick={saveAsDraft}
            disabled={isEmpty}
            data-cy="save-draft-button"
          >
            Save Draft
          </button>
          <button type="submit" className="action btn-primary" data-cy="review-transaction-button">
            Review
          </button>
        </div>
      </div>
      <ErrorDialog title="Missing fields" />
    </form>
  );
};

const NewTransaction: FC = () => <NewTransactionForm />;

export default NewTransaction;
